<template>
  <footer class="site-footer">
    <div v-if="showFooterCta" class="site-footer-cta">
      <div class="container-site site-footer-cta-inner">
        <div class="site-footer-cta-copy">
          <p class="site-footer-cta-badge">{{ homeFinalCta.eyebrow }}</p>
          <h2 class="site-footer-cta-title">{{ homeFinalCta.title }}</h2>
          <p class="site-footer-cta-desc">
            {{ homeFinalCta.description }}
          </p>
        </div>
        <div class="site-footer-cta-actions">
          <BaseButton :href="homeCtas.bookConsultation.href" variant="primary">
            {{ homeFinalCta.ctaLabel }}
          </BaseButton>
          <BaseButton :to="homeCtas.findMyVa.to" variant="outline-light">
            {{ homeCtas.findMyVa.label }}
          </BaseButton>
        </div>
      </div>
    </div>

    <div class="site-footer-main">
      <div class="container-site">
        <div class="site-footer-grid">
          <div class="site-footer-brand">
            <RouterLink to="/" class="site-footer-logo">
              <img
                v-if="logo.imageUrl"
                :src="logo.imageUrl"
                :alt="logo.alt"
                class="site-footer-logo-img"
              />
              <span
                v-else
                class="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-brand text-sm font-bold text-white shadow-soft"
              >
                {{ logo.initials }}
              </span>
              <span class="site-footer-brand-name">{{ brand.name }}</span>
            </RouterLink>
            <p class="site-footer-tagline">
              {{ brand.tagline }}
            </p>
            <ul class="mt-5 space-y-2.5">
              <li>
                <a
                  :href="`mailto:${contact.email}`"
                  class="site-footer-contact-link"
                >
                  <svg class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {{ contact.email }}
                </a>
              </li>
              <li>
                <a
                  :href="`https://wa.me/${contact.whatsapp.number}`"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="site-footer-contact-link"
                >
                  <svg class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {{ contact.whatsapp.display }}
                </a>
              </li>
              <li>
                <a
                  :href="contact.calendlyUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="site-footer-contact-link"
                >
                  <svg class="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {{ contact.calendlyLabel }}
                </a>
              </li>
            </ul>

            <div v-if="socialLinks.length" class="site-footer-social">
              <p class="site-footer-social-title">Follow us</p>
              <ul class="site-footer-social-list">
                <li v-for="link in socialLinks" :key="link.id">
                  <a
                    :href="link.href"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="site-footer-social-link"
                    :aria-label="`${brand.name} on ${link.label}`"
                  >
                    <component :is="socialIcon(link.id)" class="h-5 w-5" aria-hidden="true" />
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <nav
            v-for="column in footerColumns"
            :key="column.title"
            class="site-footer-nav"
            :aria-label="`${column.title} links`"
          >
            <h3 class="site-footer-nav-title">{{ column.title }}</h3>
            <ul class="site-footer-nav-list">
              <li v-for="link in column.links" :key="link.to || link.href">
                <a
                  v-if="link.href"
                  :href="link.href"
                  class="site-footer-link"
                >
                  {{ link.label }}
                </a>
                <RouterLink v-else :to="link.to" class="site-footer-link">
                  {{ link.label }}
                </RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div class="site-footer-bottom">
      <div class="container-site site-footer-bottom-inner">
        <p class="site-footer-copyright">
          &copy; {{ currentYear }} {{ brand.name }}. All rights reserved.
        </p>
        <div class="site-footer-bottom-links">
          <RouterLink to="/privacy-policy" class="site-footer-bottom-link">Privacy</RouterLink>
          <span class="site-footer-bottom-divider" aria-hidden="true">·</span>
          <RouterLink to="/terms-of-service" class="site-footer-bottom-link">Terms</RouterLink>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { h } from 'vue'
import { brand, contact, footerColumns, logo, homeCtas, homeFinalCta, socialLinks } from '@/config/site'
import BaseButton from '@/components/ui/BaseButton.vue'

const route = useRoute()
const currentYear = computed(() => new Date().getFullYear())
const showFooterCta = computed(() => route.name !== 'home')

const socialIcons = {
  facebook: () =>
    h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        d: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
      }),
    ]),
  instagram: () =>
    h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        d: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
      }),
    ]),
  tiktok: () =>
    h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        d: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-8.03-.01-12.05.02-18.1z',
      }),
    ]),
  linkedin: () =>
    h('svg', { fill: 'currentColor', viewBox: '0 0 24 24' }, [
      h('path', {
        d: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.062 2.062 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
      }),
    ]),
}

function socialIcon(id) {
  return socialIcons[id] || (() => null)
}
</script>
